import Link from "next/link";

import { useMemo } from "react";
import type React from "react";

import moment from "moment";
import { twMerge } from "tailwind-merge";

import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";
import { faClock } from "@fortawesome/free-solid-svg-icons/faClock";
import { faStar } from "@fortawesome/free-solid-svg-icons/faStar";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import trendingStyle from "components/InsightTrendingItems/style.module.scss";
import ComparableEntitySnippet from "components/ToolMnaTransactions/ComparableEntitySnippet";
import { useApi } from "hooks/useApi";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

export const recentPrimerInsightsUrl = () => {
  const url = new ApiEndpoint("/api/v2/insights");
  url.searchParams.append("filter[aasm-state]", "published");
  url.searchParams.append("filter[ai-generated-content-type]", "primer");
  url.searchParams.append("page[size]", "5");
  url.searchParams.append("page[number]", "1");
  url.searchParams.append("sort", "-published-at,-updated-at");
  url.searchParams.append("include", "account,primary-entity");
  url.searchParams.append(
    "fields[insights]",
    "slug,tagline,published-at,account,primary-entity"
  );
  url.searchParams.append("fields[accounts]", "avatar-url");
  url.searchParams.append("fields[entities]", "slug,pretty-name,security");

  return url.href;
};

export const recentMeetingPrepsUrl = () => {
  const url = new ApiEndpoint("/api/v3/top-investor-questions");
  url.searchParams.append("limit", "5");

  return url.href;
};

export const recentSmartscriptUrl = () => {
  const url = new ApiEndpoint("/smartscript/api/transcripts");
  url.searchParams.append("limit", "6");

  if (process.env.APP_ENV === "staging") {
    url.searchParams.append("origin", "test");
  }

  return url.href;
};

const entityLabel = (name?: string, security?: string) => {
  if (!name) return "";
  if (!security) return name;

  return `${name} (${security})`;
};

const timeAgo = (createdAt?: string) => {
  if (!createdAt) return "";

  const then = moment.utc(createdAt);
  const minutes = moment().diff(then, "minutes");
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = moment().diff(then, "hours");
  if (hours < 24) return `${hours}h ago`;

  const days = moment().diff(then, "days");
  if (days < 30) return `${days}d ago`;

  const months = moment().diff(then, "months");
  if (months < 12) return `${months}mo ago`;

  return `${moment().diff(then, "years")}y ago`;
};

// Bloomberg tickers arrive as "1168 HK": the code reads darker than the exchange.
const TickerChip = ({ ticker }: { ticker: string }) => {
  const [code, exchange] = ticker.trim().split(/\s+/);

  return (
    <span className="inline-flex shrink-0 items-center gap-[4px] rounded-[4px] bg-[#f3f4f6] px-[6px] py-[2px] text-[12px] leading-[16px]">
      <span className="font-medium text-[#4b5563]">{code}</span>
      {exchange && <span className="text-[#9ca3af]">{exchange}</span>}
    </span>
  );
};

const RecentSmartscriptItem = ({
  smartscript,
  onSelect,
}: {
  smartscript: any;
  onSelect?: (_transcriptId: string | number) => void;
}) => {
  const hasRating = (smartscript.rating_count ?? 0) > 0;

  const itemClassName =
    "group flex items-center gap-[10px] rounded-[6px] px-[6px] py-[8px] no-underline hover:bg-[#f9fafb]";

  const content = (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-[8px]">
          <span className="truncate text-[14px] font-semibold text-[#1f2937]">
            {smartscript.entity_discussed}
          </span>
          {smartscript.bloomberg_ticker && (
            <TickerChip ticker={smartscript.bloomberg_ticker} />
          )}
        </div>
        <div className="mt-[4px] flex items-center gap-[6px] text-[12px] text-[#9ca3af]">
          {smartscript.caller_type && (
            <>
              <span className="truncate">{smartscript.caller_type}</span>
              <span aria-hidden>·</span>
            </>
          )}
          <span className="flex shrink-0 items-center gap-[4px]">
            <FontAwesomeIcon icon={faClock} />
            {timeAgo(smartscript.created_at)}
          </span>
          {hasRating && (
            <>
              <span aria-hidden>·</span>
              <span className="flex shrink-0 items-center gap-[4px] text-[#4b5563]">
                <FontAwesomeIcon icon={faStar} className="text-[#f5b301]" />
                {Number(smartscript.rating_avg).toFixed(1)}
              </span>
            </>
          )}
        </div>
      </div>
      <FontAwesomeIcon
        icon={faChevronRight}
        className="shrink-0 text-[12px] text-[#d1d5db] group-hover:text-[#9ca3af]"
      />
    </>
  );

  // Without a handler to open the transcript in a modal, fall back to the
  // standalone SmartScripts app.
  if (!onSelect) {
    return (
      <Link
        href={`https://smartscripts.smartkarma.com/t/${smartscript.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className={itemClassName}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(smartscript.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(smartscript.id);
        }
      }}
      className={twMerge(itemClassName, "cursor-pointer")}
    >
      {content}
    </div>
  );
};

const RecentItem = ({ href, title }: { href: string; title: string }) => (
  <div className={twMerge(trendingStyle.trendingItem, "!p-0")}>
    <div className={trendingStyle.desc}>
      <div>
        <Link
          href={href}
          className="text-[#1f1f20] no-underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {title}
        </Link>
      </div>
    </div>
  </div>
);

const RecentSectionShell = ({
  title = "Recently Generated",
  children,
  contentClassName,
  moreHref,
  moreLabel,
}: React.PropsWithChildren<{
  contentClassName?: string;
  title?: string;
  moreHref?: string;
  moreLabel?: string;
}>) => (
  <div className="pt-[16px]">
    <div className="relative m-0 mb-[5px] flex items-center text-[12px] font-medium uppercase leading-[30px] tracking-[1px] text-gray-gray">
      <div className="flex-1">{title}</div>
      {moreHref && moreLabel && (
        <Link
          href={moreHref}
          className="pull-right normal-case leading-[30px] text-smart-teal no-underline"
        >
          {moreLabel} »
        </Link>
      )}
    </div>
    <div className={twMerge("flex flex-col space-y-[10px]", contentClassName)}>
      {children}
    </div>
  </div>
);

export const RecentPrimersSection = () => {
  const url = recentPrimerInsightsUrl();
  const { data, isLoading } = useApi(url, baseFetcher, {
    revalidateOnFocus: false,
  });

  const primers = useMemo(() => data?.data?.slice?.(0, 5) || [], [data]);

  if (isLoading || !primers.length) return null;

  return (
    <RecentSectionShell>
      {primers.map((primer: any) => {
        const primaryEntity = primer.primaryEntity;

        return (
          <RecentItem
            key={primer.slug}
            href={`/insights/${primer.slug}`}
            title={
              primer.tagline ||
              `${entityLabel(
                primaryEntity?.prettyName || "Company",
                primaryEntity?.security
              )} Primer`
            }
          />
        );
      })}
    </RecentSectionShell>
  );
};

export const RecentMeetingPrepsSection = () => {
  const url = recentMeetingPrepsUrl();
  const { data, isLoading } = useApi(url, baseFetcher, {
    revalidateOnFocus: false,
  });

  const meetingPreps = useMemo(() => data?.slice?.(0, 5) || [], [data]);

  if (isLoading || !meetingPreps.length) return null;

  return (
    <RecentSectionShell contentClassName="flex-row flex-wrap gap-3 space-y-0">
      {meetingPreps.map((prep: any) => {
        const entitySlug = prep.entity_slug || prep.entity_id;
        const entity = {
          id: prep.entity_id,
          pretty_name: prep.entity_name,
          bloomberg_ticker: prep.entity_security || prep.entity_ticker,
        };

        return (
          <ComparableEntitySnippet
            key={prep.id}
            href={`/entities/${entitySlug}?top-investor-questions=true`}
            item={entity}
            newTab
          />
        );
      })}
    </RecentSectionShell>
  );
};

export const RecentSmartscriptSection = ({
  contentOnly,
  onSelectSmartscript,
}: {
  contentOnly?: boolean;
  onSelectSmartscript?: (_transcriptId: string | number) => void;
}) => {
  const url = recentSmartscriptUrl();
  const { data, isLoading } = useApi(url, baseFetcher, {
    revalidateOnFocus: false,
  });

  const smartscripts = useMemo(
    () => data?.results?.slice?.(0, 6) || [],
    [data]
  );

  if (isLoading || !smartscripts.length) return null;

  return (
    <RecentSectionShell
      title="Recent Transcripts"
      contentClassName={twMerge(
        !contentOnly
          ? "grid grid-cols-1 gap-x-[16px] gap-y-[4px] space-y-0 md:grid-cols-2"
          : ""
      )}
    >
      {smartscripts.map((smartscript: any) => (
        <RecentSmartscriptItem
          key={smartscript.id}
          smartscript={smartscript}
          onSelect={onSelectSmartscript}
        />
      ))}
    </RecentSectionShell>
  );
};

export const RecentComparablesSection = () => {
  const { data, isLoading } = useApi(
    "/api/v3/merging-acquisitions",
    baseFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const comparables = useMemo(() => data?.slice?.(0, 5) || [], [data]);

  if (isLoading || !comparables.length) return null;

  return (
    <RecentSectionShell
      contentClassName="flex-row flex-wrap gap-3 space-y-0"
      title="Recently Generated"
      moreHref="/tools/comparables"
      moreLabel="More"
    >
      {comparables.map((item: any) => {
        const entity = item.entity;
        if (!entity?.id) return null;

        return (
          <ComparableEntitySnippet
            key={item.id || entity.id}
            href={`/tools/comparables?entity-id=${entity.id}`}
            item={item}
            newTab
          />
        );
      })}
    </RecentSectionShell>
  );
};
